import { Box, Button, MenuItem, Typography } from '@mui/material'
import { type GridRenderCellParams } from '@mui/x-data-grid'
import { StyledMenu } from 'components/StyledMenu/StyledMenu'
import { type SweepstakeWinnerStatusType } from '../../../../types/api'
import ListBullet from 'assets/format_list_bulleted.svg'
import { useState } from 'react'
import './styles.css'
import { usePutSweepstakeWinner } from 'hooks/api/Sweepstake/usePutSweepstakeWinner'
import { SweepstakesValidateWinnerModal } from './SweepstakesValidateWinnerModal'
import { useQueryClient } from '@tanstack/react-query'
import { getSweepstakeDrawingInfoQueryKey } from 'hooks/api/useGetSweepstakeByIdWithDrawingInfo'
import { usePutSweepstakeIfAllWinnersAwarded } from 'hooks/api/Sweepstake/usePutSweepstakeIfAllWinnersAwarded'
import { SweepstakeNotifyWinnersModal } from 'components/Sweepstakes/SweepstakeNotifyWinnersModal'
// I switched this to import from LocationPrizeMenu which is located under LocationSweepstakes because I thought these menus were going to be the same
// but we might end up needing to have different menus for each
// so I renamed the file that was being used to import RedrawWinnerMenuItem here to "JJSweepstakesLocationPrizeMenu.tsx"
import { RedrawWinnerMenuItem } from 'pages/Contests/LocationSweepstakes/LocationSweepstakesWinnersPage/LocationPrizeMenu'

export const GrandPrizeMenu = ({
  params,
  setIsIndividualRedrawModalOpen,
  contestId,
  sweepstakeName,
}: {
  params: GridRenderCellParams
  setIsIndividualRedrawModalOpen: () => void
  contestId: number
  sweepstakeName: string
}) => {
  const qc = useQueryClient()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedSweepstakeWinnerId, setSelectedSweepstakeWinnerId] = useState<
    number | undefined
  >()
  const [notifyWinnersModalOpen, setNotifyWinnersModalOpen] = useState(false)
  const [
    isSweepstakesValidateWinnerModalOpen,
    setIsSweepstakesValidateWinnerModalOpen,
  ] = useState(false)
  const toggleSweepstakesValidateWinnerModalOpen = () =>
    setIsSweepstakesValidateWinnerModalOpen(
      !isSweepstakesValidateWinnerModalOpen
    )
  const { mutateAsync: mutateSweepstakeAsync } =
    usePutSweepstakeIfAllWinnersAwarded({
      sweepstakeId: contestId,
      onError: () => {},
      onSuccess: () => {},
    })

  const { mutateAsync } = usePutSweepstakeWinner({
    onError: () => {},
    onSuccess: async (data) => {
      await qc.invalidateQueries({
        queryKey: getSweepstakeDrawingInfoQueryKey(contestId),
        exact: true,
      })
      setAnchorEl(null)
      // If winner has been awarded, check if all winners are awarded and mark contest as "Closed"
      if (data.status === 'Awarded') {
        await mutateSweepstakeAsync()
      }
    },
  })

  const toggleMenu = (event: React.MouseEvent<HTMLElement>, id: number) => {
    event.stopPropagation()
    if (anchorEl === null) {
      setSelectedSweepstakeWinnerId(id)
      setAnchorEl(event.currentTarget)
    } else {
      setAnchorEl(null)
      setSelectedSweepstakeWinnerId(undefined)
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = anchorEl !== null

  const status: SweepstakeWinnerStatusType = params.row.winnerStatus

  if (status === 'NoWinner') {
    return null
  }

  return (
    <>
      <Button
        onClick={(e) => {
          e.stopPropagation()
          toggleMenu(e, params.row.id)
        }}
        style={{
          minWidth: '50px',
        }}
      >
        <img src={ListBullet} width={4} height={16} alt="options icon" />
      </Button>
      <StyledMenu
        sx={{ mt: 0 }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        {[
          status === 'GrandPrizeValidation' && (
            <MenuItem
              key="validateWinner"
              onClick={() => setIsSweepstakesValidateWinnerModalOpen(true)}
            >
              <Box className="py-2">
                <Typography variant="body-2">Validate Winner</Typography>
              </Box>
            </MenuItem>
          ),

          ...(status === 'WinnerValidation'
            ? [
                <MenuItem
                  key="notifyWinner"
                  onClick={() => {
                    handleClose()
                    setNotifyWinnersModalOpen(true)
                  }}
                >
                  <Box className="py-2">
                    <Typography variant="body-2">Notify Winner</Typography>
                  </Box>
                </MenuItem>,
                <MenuItem
                  key="affidavitReceived"
                  onClick={async () =>
                    await mutateAsync({
                      req: {
                        id: selectedSweepstakeWinnerId,
                        status: 'AffidavitReceived',
                        sweepstakeDrawingId: params.row.sweepstakeDrawingId,
                        contestEntryId: params.row.contestEntryId,
                        patronId: params.row.patronId,
                        dateDrawn: params.row.drawnDate,
                        notes: params.row.notes,
                        contestWinnerId: params.row.contestWinnerId,
                        affidavitCompletedAt: null, // Set as null because the backend code will set it to current date time if null
                      },
                    })
                  }
                >
                  <Box className="py-2">
                    <Typography variant="body-2">Affidavit Received</Typography>
                  </Box>
                </MenuItem>,
              ]
            : []),

          status === 'WinnerNotified' && (
            <>
              <MenuItem
                key="affidavitReceivedSingle"
                onClick={async () =>
                  await mutateAsync({
                    req: {
                      id: selectedSweepstakeWinnerId,
                      status: 'AffidavitReceived',
                      sweepstakeDrawingId: params.row.sweepstakeDrawingId,
                      contestEntryId: params.row.contestEntryId,
                      patronId: params.row.patronId,
                      dateDrawn: params.row.drawnDate,
                      notes: params.row.notes,
                      contestWinnerId: params.row.contestWinnerId,
                      affidavitCompletedAt: null, // Set as null because the backend code will set it to current date time if null
                    },
                  })
                }
              >
                <Box className="py-2">
                  <Typography variant="body-2">Affidavit Received</Typography>
                </Box>
              </MenuItem>

              <MenuItem
                key="notifyWinner"
                onClick={() => {
                  handleClose()
                  setNotifyWinnersModalOpen(true)
                }}
              >
                <Box className="py-2">
                  <Typography variant="body-2">Notify Winner</Typography>
                </Box>
              </MenuItem>
            </>
          ),

          status === 'AffidavitReceived' && (
            <MenuItem
              key="sentToAP"
              onClick={async () =>
                await mutateAsync({
                  req: {
                    id: selectedSweepstakeWinnerId,
                    status: 'SentToAP',
                    sweepstakeDrawingId: params.row.sweepstakeDrawingId,
                    contestEntryId: params.row.contestEntryId,
                    patronId: params.row.patronId,
                    dateDrawn: params.row.drawnDate,
                    notes: params.row.notes,
                    contestWinnerId: params.row.contestWinnerId,
                    affidavitCompletedAt: params.row.affidavitCompletedAt,
                  },
                })
              }
            >
              <Box className="py-2">
                <Typography variant="body-2">Sent To AP</Typography>
              </Box>
            </MenuItem>
          ),

          status === 'SentToAP' && (
            <MenuItem
              key="awarded"
              onClick={async () =>
                await mutateAsync({
                  req: {
                    id: selectedSweepstakeWinnerId,
                    status: 'Awarded',
                    sweepstakeDrawingId: params.row.sweepstakeDrawingId,
                    contestEntryId: params.row.contestEntryId,
                    patronId: params.row.patronId,
                    dateDrawn: params.row.drawnDate,
                    notes: params.row.notes,
                    contestWinnerId: params.row.contestWinnerId,
                    affidavitCompletedAt: params.row.affidavitCompletedAt,
                  },
                })
              }
            >
              <Box className="py-2">
                <Typography variant="body-2">Awarded</Typography>
              </Box>
            </MenuItem>
          ),

          <RedrawWinnerMenuItem
            key="redrawWinner"
            handleClose={handleClose}
            setIsIndividualRedrawModalOpen={setIsIndividualRedrawModalOpen}
          />,
        ].filter(Boolean)}
      </StyledMenu>
      <SweepstakesValidateWinnerModal
        winnerName={params.row.winnerName}
        tierName={params.row.tierName}
        isModalOpen={isSweepstakesValidateWinnerModalOpen}
        toggleIsOpen={toggleSweepstakesValidateWinnerModalOpen}
        onConfirm={async () => {
          toggleSweepstakesValidateWinnerModalOpen()
          await mutateAsync({
            req: {
              id: selectedSweepstakeWinnerId,
              status: 'WinnerValidation',
              sweepstakeDrawingId: params.row.sweepstakeDrawingId,
              contestEntryId: params.row.contestEntryId,
              drawingSequence: params.row.drawingSequence,
              dateDrawn: params.row.drawnDate,
              patronId: params.row.patronId,
              notes: params.row.notes,
              contestWinnerId: params.row.contestWinnerId,
            },
          })
          handleClose()
        }}
        onCancel={() => {
          toggleSweepstakesValidateWinnerModalOpen()
          handleClose()
        }}
      />
      <SweepstakeNotifyWinnersModal
        sweepstakeName={sweepstakeName}
        isModalOpen={notifyWinnersModalOpen}
        toggleIsOpen={() => setNotifyWinnersModalOpen(!notifyWinnersModalOpen)}
        selectedWinners={[
          {
            locationName: params.row.leName,
            licenseNumber: params.row.leLicenseNumber,
            winnerName: params.row.winnerName,
            sweepstakesWinner: params.row,
            drawingSequence: params.row.drawingSequence ?? 0,
          },
        ]}
        isLocationSweepstake={false}
        invalidateQueryKeys={[getSweepstakeDrawingInfoQueryKey(contestId)]}
      />
    </>
  )
}
